import { TFunction } from 'i18next';
import { v4 } from 'uuid';

/* eslint-disable import/no-cycle */
import { translateScheduleElementName } from '../../../components/MySchedule/transformData';
import {
  QueueChannelType,
  ScheduleElement as DefaultScheduleElement,
  ScheduleItem,
  ScheduleItemExtended,
} from '../../../graphql/components';
import { formatScheduleElementRGBAColor } from '../../../utils/chart';
import { getDayIndexesForElement } from '../SchedulingComposer/reducer';
import { MIN_15, MIN_15_DROP_ELEMENT_WIDTH, HOURS_24 } from '../config';
import { ChartScheduleElement, ProductionChartScheduleElement, ScheduleElement } from '../types';

export const transformScheduleElementsData = (
  scheduledItems: ScheduleItem[],
  scheduleElements: ScheduleElement[],
  days: Date[],
  timeZone: string
) =>
  scheduledItems.reduce((usedScheduleElements: ChartScheduleElement[], scheduledItem) => {
    const scheduleElement = scheduleElements.find((element) => element.id === scheduledItem.scheduleElementId);
    if (!scheduleElement || !scheduledItem.agentId) {
      return usedScheduleElements;
    }
    // TODO: add timezone offset to elements coming from BE once dealing with timezone inconsistency
    const start = new Date(scheduledItem.dateTimeRange.startDateTime).getTime();
    const end = new Date(scheduledItem.dateTimeRange.endDateTime).getTime();
    const id = v4();

    usedScheduleElements.push({
      start,
      end,
      id,
      agentId: scheduledItem.agentId,
      scheduleElementId: scheduleElement.id,
      scheduleElementName: scheduleElement.name,
      scheduleElementNameSize: scheduleElement.nameSize,
      scheduleElementOriginalName: scheduleElement.originalName,
      key: id,
      color: formatScheduleElementRGBAColor(scheduleElement.color),
      paid: scheduleElement.paid,
      paidTimeOff: scheduleElement.paidTimeOff,
      pinnable: scheduleElement.pinnable,
      pinned: scheduledItem.pinned,
      productive: scheduleElement.productive,
      selected: false,
      wholeDayElement: scheduleElement.defaultDuration >= HOURS_24,
      shift: scheduleElement.shift,
      dayIndexes: getDayIndexesForElement(start, end, timeZone, days),
    });
    return usedScheduleElements;
  }, []);

export const transformProductionScheduleElementsData = (
  productionScheduleItems: ScheduleItemExtended[],
  scheduleElements: ScheduleElement[],
  days: Date[],
  timeZone: string
) =>
  productionScheduleItems.reduce(
    (productionScheduleElements: ProductionChartScheduleElement[], productionScheduleItem) => {
      const scheduleElement = scheduleElements.find(
        (element) => element.id === productionScheduleItem.scheduleElementId
      );
      if (!scheduleElement || !productionScheduleItem.agentId) {
        return productionScheduleElements;
      }
      const start = new Date(productionScheduleItem.dateTimeRange.startDateTime).getTime();
      const end = new Date(productionScheduleItem.dateTimeRange.endDateTime).getTime();
      const id = v4();

      productionScheduleElements.push({
        start,
        end,
        id,
        agentId: productionScheduleItem.agentId,
        color: formatScheduleElementRGBAColor(scheduleElement.color),
        dayIndexes: getDayIndexesForElement(start, end, timeZone, days),
        key: id,
        paid: scheduleElement.paid,
        paidTimeOff: scheduleElement.paidTimeOff,
        pinnable: scheduleElement.pinnable,
        pinned: productionScheduleItem.pinned,
        productive: scheduleElement.productive,
        queueIds: productionScheduleItem.sourceSchedule?.scheduleQueuesIds ?? [],
        ptoRequestId: productionScheduleItem.ptoRequestId || undefined,
        selected: false,
        shift: scheduleElement.shift,
        scheduleElementId: scheduleElement.id,
        scheduleElementName: scheduleElement.name,
        scheduleElementNameSize: scheduleElement.nameSize,
        scheduleElementOriginalName: scheduleElement.originalName,
        sourceScheduleId: productionScheduleItem.sourceSchedule?.scheduleId || undefined,
        wholeDayElement: scheduleElement.defaultDuration >= HOURS_24,
      });
      return productionScheduleElements;
    },
    []
  );

export const countOfUpperAndLowerCasedLetters = (text: string) =>
  text.split('').reduce(
    (previousState, currentChar) => {
      if (currentChar === currentChar.toUpperCase()) {
        return {
          ...previousState,
          upperCasedLettersCount: previousState.upperCasedLettersCount + 1,
        };
      }

      return {
        ...previousState,
        lowerCasedLettersCount: previousState.lowerCasedLettersCount + 1,
      };
    },
    { upperCasedLettersCount: 0, lowerCasedLettersCount: 0 }
  );

export const approximateScheduleElementNameSize = (elementName: string, allLettersUpperCased = true) => {
  const splitElementName = elementName.split(' ');
  const emptyCharsInName = splitElementName.length - 1;
  const elementNameWithoutSpaces = splitElementName.join('');
  const splitElementNameCharsLength = elementNameWithoutSpaces.length;

  if (allLettersUpperCased) {
    // 10 px for 1 letter, 2px for empty space character
    return splitElementNameCharsLength * 10 + emptyCharsInName * 2;
  }

  const { lowerCasedLettersCount, upperCasedLettersCount } = countOfUpperAndLowerCasedLetters(elementNameWithoutSpaces);

  // 10 px for 1 upper-cased letter, 6px for 1 lower-cased letter, 2px for empty space character
  return upperCasedLettersCount * 10 + lowerCasedLettersCount * 6 + emptyCharsInName * 2;
};

export const transformSourceScheduleElements = (scheduleElements: DefaultScheduleElement[], t: TFunction) =>
  scheduleElements.map((element) => {
    // value of default duration converted to milliseconds as BE provide us with values in seconds
    const defaultDuration = element.defaultDuration * 1000;

    // constrain length of dropped elements longer than 24 hours to 45 minutes
    const defaultDropDuration = defaultDuration < HOURS_24 ? defaultDuration : MIN_15 * 3;

    const elementName = translateScheduleElementName(t, element);

    return {
      ...element,
      defaultDuration,
      defaultDropElementWidth: (defaultDropDuration / MIN_15) * MIN_15_DROP_ELEMENT_WIDTH,
      name: elementName,
      originalName: element.name,
      nameSize: approximateScheduleElementNameSize(elementName),
    };
  });

export const deriveChannelTypeFromElement = (element: ChartScheduleElement) => {
  const channelType = Object.keys(QueueChannelType);
  const selectedType = channelType.find((type) => type === element.scheduleElementOriginalName);

  if (!selectedType) {
    return undefined;
  }

  return QueueChannelType[selectedType as keyof typeof QueueChannelType];
};
